// Two-input city/state capture row. Used inline on every LeadList row
// AND on PartyDetail. Autosaves on blur via partyApi.patchSkipTraceLocation,
// no explicit save button — matches the AddPhoneInline / Notes feel.
//
// Keyboard flow (the design point that determines whether this scales
// to 100 rows in 20 minutes vs an hour):
//   - Tab order: city → state (within row)
//   - Enter on either input → commit + jump to NEXT row's city
//   - State input auto-uppercases on type, max length 2
//   - Inputs preserve raw typing; blur normalizes and saves
//
// Parent passes `focusNext`: a callback that focuses the city input of
// the next visible row (computed in LeadList from the flat ordered
// list of items). Returns false when this is the last row — we keep
// focus on the current state input rather than racing somewhere odd.

import { useEffect, useRef, useState } from 'react';
import { partyApi } from '../services/partyApi';

interface SkipTraceInputsProps {
  partyId: string;
  initialCity: string | null;
  initialState: string | null;
  onSaved: (next: { skip_trace_city: string | null; skip_trace_state: string | null }) => void;
  /** Called on Enter or on Tab past the state input. Should focus the
   *  next row's city input; return false to indicate "no next row" and
   *  this component will keep focus where it is. */
  focusNext?: () => boolean;
  /** Lifts the city input ref to the parent so Enter on row N-1's
   *  state input can target row N's city input. Optional — without
   *  this hook, Enter-to-next still works via focusNext. */
  registerCityRef?: (partyId: string, el: HTMLInputElement | null) => void;
}

export function SkipTraceInputs(props: SkipTraceInputsProps) {
  const { partyId, initialCity, initialState, onSaved, focusNext, registerCityRef } = props;
  const [city,  setCity]  = useState<string>(initialCity  ?? '');
  const [stateVal, setStateVal] = useState<string>(initialState ?? '');
  const [savingCity, setSavingCity]   = useState(false);
  const [savingState, setSavingState] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cityRef  = useRef<HTMLInputElement | null>(null);
  const stateRef = useRef<HTMLInputElement | null>(null);

  // Track the last-saved values so blur doesn't fire a no-op PATCH when
  // the operator tabbed through without editing.
  const lastSavedCityRef  = useRef<string>(initialCity  ?? '');
  const lastSavedStateRef = useRef<string>(initialState ?? '');

  // Re-hydrate when the underlying party changes (e.g. parent reloaded
  // the lead list after a status change and the item identity stayed
  // but the values moved).
  useEffect(() => {
    setCity(initialCity ?? '');
    lastSavedCityRef.current = initialCity ?? '';
  }, [initialCity, partyId]);
  useEffect(() => {
    setStateVal(initialState ?? '');
    lastSavedStateRef.current = initialState ?? '';
  }, [initialState, partyId]);

  // Lift the city ref to the parent. Re-run when partyId changes so the
  // parent always has the latest <input> handle keyed by partyId.
  useEffect(() => {
    if (!registerCityRef) return;
    registerCityRef(partyId, cityRef.current);
    return () => registerCityRef(partyId, null);
  }, [partyId, registerCityRef]);

  async function saveCity() {
    const next = city.trim() === '' ? null : city;
    if (next === lastSavedCityRef.current) return;
    setSavingCity(true);
    setErr(null);
    try {
      const r = await partyApi.patchSkipTraceLocation(partyId, { skip_trace_city: next });
      lastSavedCityRef.current = next ?? '';
      onSaved({ skip_trace_city: r.skip_trace_city, skip_trace_state: r.skip_trace_state });
    } catch (e: any) {
      setErr(e?.message ?? 'save failed');
    } finally {
      setSavingCity(false);
    }
  }

  async function saveState() {
    const raw = stateVal.trim().toUpperCase();
    const next = raw === '' ? null : raw;
    if (next === lastSavedStateRef.current) return;
    if (next !== null && next.length !== 2) {
      setErr('state must be 2 chars');
      return;
    }
    setSavingState(true);
    setErr(null);
    try {
      const r = await partyApi.patchSkipTraceLocation(partyId, { skip_trace_state: next });
      lastSavedStateRef.current = next ?? '';
      onSaved({ skip_trace_city: r.skip_trace_city, skip_trace_state: r.skip_trace_state });
    } catch (e: any) {
      setErr(e?.message ?? 'save failed');
    } finally {
      setSavingState(false);
    }
  }

  const cityEmpty  = city.trim() === '';
  const stateEmpty = stateVal.trim() === '';
  // Accent ring when empty — the eye lands on "needs data". When the
  // operator has typed something, fade to neutral so completed rows
  // recede visually.
  const cityCls = (
    'w-full bg-bg-input border rounded px-2 py-1 text-[12px] font-mono outline-none transition-colors ' +
    (cityEmpty ? 'border-accent/40 focus:border-accent' : 'border-border focus:border-accent')
  );
  const stateCls = (
    'w-full bg-bg-input border rounded px-2 py-1 text-[12px] font-mono outline-none uppercase text-center transition-colors ' +
    (stateEmpty ? 'border-accent/40 focus:border-accent' : 'border-border focus:border-accent')
  );

  return (
    <>
      <input
        ref={cityRef}
        type="text"
        value={city}
        onChange={e => setCity(e.target.value)}
        onBlur={saveCity}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            // Commit + move to state input.
            saveCity();
            stateRef.current?.focus();
            stateRef.current?.select();
          }
        }}
        placeholder="City"
        className={cityCls}
      />
      <input
        ref={stateRef}
        type="text"
        value={stateVal}
        onChange={e => {
          const next = e.target.value.toUpperCase().slice(0, 2);
          setStateVal(next);
        }}
        onBlur={saveState}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            saveState();
            if (focusNext) {
              const moved = focusNext();
              if (!moved) stateRef.current?.blur();
            }
          }
        }}
        placeholder="ST"
        maxLength={2}
        className={stateCls}
      />
      {(savingCity || savingState) && (
        <span className="text-[9px] text-accent font-mono whitespace-nowrap" aria-live="polite">saving…</span>
      )}
      {err && (
        <span className="text-[9px] text-red font-mono" title={err}>{err}</span>
      )}
    </>
  );
}

/**
 * Small dot that turns green when both city + state are populated.
 * Drives the operator's "row is done" feedback.
 */
export function SkipTraceReadyDot(props: { city: string | null; state: string | null; className?: string }) {
  const ready = !!(props.city && props.city.trim() && props.state && props.state.trim());
  const cls   = props.className ?? '';
  return (
    <span
      className={(ready ? 'text-green' : 'text-text-ghost') + ' text-[12px] leading-none align-middle ' + cls}
      title={ready ? 'Skip-trace ready (city + state captured)' : 'Needs city + state for EnformionGO lookup'}
      aria-label={ready ? 'skip-trace ready' : 'needs location'}
    >
      ●
    </span>
  );
}
